/* ============================================================
   AMP3 redesign — basket drawer and basket page.
   Works alongside the theme's cart-drawer.js / cart-items.js.
   1. "Added to your basket" appears only after a successful add
      (the theme's on:cart:add / on:line-item:change events), never
      when the basket is simply opened from the header.
   2. Undo after removing a line: re-adds the same variant, quantity,
      properties and selling plan via the Cart API, then re-renders.
   3. "Customers also choose" (basket page and drawer): what customers
      bought with the key basket items (amp3.bought_together, from orders),
      then Search & Discovery complementary products (highest line value
      first), optionally topped up with related products, with an
      Add button for single-option products.
   4. "Goes well with" (basket page and drawer): the Kaching bundle for a
      basket item. Kaching keeps the offer, prices and basket add: the
      button opens that product's bundle section with the bar selected,
      and amp3-bundles.js swaps the separate item for the bundle.
   ============================================================ */
(function () {
  'use strict';
  if (window.a3Basket) return;
  window.a3Basket = true;

  /* ---------- 1. Added confirmation ---------- */
  var pendingAdd = null;
  function rememberAdd(evt) {
    if (!evt.target.closest || !evt.target.closest('product-form')) return;
    pendingAdd = { variantId: String(evt.detail && evt.detail.variantId), at: Date.now() };
  }
  document.addEventListener('on:cart:add', rememberAdd);
  document.addEventListener('on:line-item:change', rememberAdd);

  function onDrawerOpen(drawer) {
    var added = drawer.querySelector('[data-a3b-added]');
    drawer.querySelectorAll('.cart-item.is-added').forEach(function (row) { row.classList.remove('is-added'); });
    if (!added) return;
    var fresh = pendingAdd && Date.now() - pendingAdd.at < 8000;
    added.hidden = !fresh;
    if (fresh) {
      var row = drawer.querySelector('.cart-item[data-variant-id="' + pendingAdd.variantId + '"]');
      if (row) {
        row.classList.add('is-added');
        var name = row.querySelector('.js-item-name');
        if (name) added.querySelector('span').textContent = 'Added ' + name.textContent.trim() + ' to your basket';
      }
    }
    pendingAdd = null;
  }

  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer.a3b-drawer');
    if (!drawer) return;
    new MutationObserver(function () {
      if (drawer.hasAttribute('open')) { onDrawerOpen(drawer); fillBoxes(); }
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  /* ---------- 2. Undo removal ---------- */
  var lastRemoved = null;

  function undoBox(scope) {
    var box = scope.querySelector('[data-a3b-undo]');
    if (!box) {
      // Drawer: create the bar above the items (outside the re-rendered cart-items).
      var host = scope.querySelector('.cart-drawer__content');
      if (!host) return null;
      box = document.createElement('p');
      box.className = 'a3b-undo';
      box.setAttribute('data-a3b-undo', '');
      box.setAttribute('role', 'status');
      box.hidden = true;
      box.innerHTML = '<span data-a3b-undo-text></span> <button type="button" data-a3b-undo-btn>Undo</button>';
      host.prepend(box);
    }
    return box;
  }

  function showUndo(scope, text, withButton) {
    var box = undoBox(scope);
    if (!box) return;
    box.querySelector('[data-a3b-undo-text]').textContent = text;
    var btn = box.querySelector('[data-a3b-undo-btn]');
    btn.hidden = !withButton;
    box.hidden = false;
    if (withButton) setTimeout(function () { btn.focus(); }, 50);
  }

  document.addEventListener('click', function (evt) {
    var remove = evt.target.closest('.js-remove-item');
    if (!remove) return;
    var row = remove.closest('.cart-item');
    var data = row && row.querySelector('[data-a3b-line]');
    if (!data) return;
    try { lastRemoved = JSON.parse(data.textContent); } catch (e) { lastRemoved = null; return; }
    lastRemoved.drawer = remove.closest('cart-drawer');
    lastRemoved.scope = lastRemoved.drawer || document.querySelector('.a3b-page') || document;
    // Let the theme remove the line, then offer undo once the basket has re-rendered.
    setTimeout(function () {
      if (lastRemoved) showUndo(lastRemoved.scope, 'Removed ' + lastRemoved.title + '.', true);
    }, 900);
  }, true);

  document.addEventListener('click', async function (evt) {
    var btn = evt.target.closest('[data-a3b-undo-btn]');
    if (!btn || !lastRemoved) return;
    var item = lastRemoved;
    lastRemoved = null;
    btn.disabled = true;
    var line = { id: item.id, quantity: item.quantity };
    if (item.properties && Object.keys(item.properties).length) line.properties = item.properties;
    if (item.selling_plan) line.selling_plan = item.selling_plan;
    try {
      var res = await fetch((theme.routes.cartAdd || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [line] })
      });
      var json = await res.json();
      if (!res.ok) throw new Error(json.description || json.message || 'Error');
      btn.disabled = false;
      var drawer = item.drawer;
      if (drawer) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      } else {
        var cartItems = document.querySelector('cart-items');
        if (!cartItems || cartItems.dataset.empty === 'true') { window.location.reload(); return; }
        await cartItems.refresh();
        document.querySelectorAll('[data-a3b-undo]').forEach(function (b) { b.hidden = true; });
        var restored = document.querySelector('.cart-item[data-variant-id="' + item.id + '"] .js-item-name');
        if (restored) restored.focus();
      }
      if (drawer) setTimeout(function () { showUndo(drawer, item.title + ' is back in your basket.', false); }, 700);
    } catch (e) {
      btn.disabled = false;
      showUndo(item.scope, 'Sorry, we couldn’t put ' + item.title + ' back (' + e.message + '). Please add it again from its product page.', false);
    }
  });


  /* ---------- shared ---------- */
  // Open-box / clearance listings have their own "different way to own it" slot.
  var OPEN_BOX = /\((box opened|damaged|missing|ex[ -]?(sample|demo|display)|device only|device and|b[ -]?grade|open box|wobbly|scratch)/i;

  function money(cents) {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: currency }).format(cents / 100);
  }
  function esc(text) {
    return String(text).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }
  function sized(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }
  var memo = {};   // url -> Promise (per page view)
  function getJSON(url) {
    if (!memo[url]) memo[url] = fetch(url).then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); }).catch(function () { delete memo[url]; return null; });
    return memo[url];
  }
  function getCart() {
    return fetch((theme.routes.cart || '/cart') + '.js').then(function (r) { return r.json(); });
  }
  function isBundleLine(item) { return !!(item.properties && item.properties.__kaching_bundles); }

  var offered = {};   // products in the "Goes well with" offer, so they aren't suggested again at full price

  /* ---------- 3. Customers also choose ---------- */
  function recCard(p, quickAdd) {
    var img = sized(p.featured_image, 300);
    var from = p.price_varies ? '<small>From</small> ' : '';
    var was = p.compare_at_price > p.price ? ' <del>' + money(p.compare_at_price) + '</del>' : '';
    var single = p.variants && p.variants.length === 1 && p.variants[0].available;
    var action = quickAdd && single
      ? '<button type="button" class="a3b-rec__add" data-a3b-quick-add="' + p.variants[0].id + '" data-title="' + esc(p.title) + '">Add<span class="a3-sr-only"> ' + esc(p.title) + ' to your basket</span></button>'
      : '<a class="a3b-rec__link" href="' + esc(p.url) + '">' + (quickAdd ? 'Choose options' : 'Take a look') + '<span class="a3-sr-only">: ' + esc(p.title) + '</span> →</a>';
    return '<li class="a3b-rec"><a href="' + esc(p.url) + '" class="a3b-rec__image" tabindex="-1" aria-hidden="true">' +
      (img ? '<img src="' + esc(img) + '" alt="" loading="lazy" width="150" height="150">' : '') + '</a>' +
      '<div class="a3b-rec__info"><p class="a3b-rec__brand">' + esc(p.vendor) + '</p>' +
      '<p class="a3b-rec__title"><a href="' + esc(p.url) + '">' + esc(p.title) + '</a></p>' +
      '<p class="a3b-rec__price">' + from + money(p.price) + was + '</p>' + action + '</div></li>';
  }

  async function fetchRecs(base, productId, intent) {
    var data = await getJSON(base + '.json?product_id=' + productId + '&intent=' + intent + '&limit=8');
    return (data && data.products) || [];
  }

  /** "Bought together" from real orders (product metafield amp3.bought_together via ?view=a3-also); empty without data. */
  async function fetchBought(handle) {
    if (!handle) return [];
    var data = await getJSON('/products/' + handle + '?view=a3-also');
    return (data && data.products) || [];
  }

  async function pickRecs(cart, box) {
    var limit = Number(box.dataset.limit) || 3;
    var base = box.dataset.url || '/recommendations/products';
    var inCart = {};
    cart.items.forEach(function (i) { inCart[i.product_id] = true; });
    // Key products: highest line value first, ignoring open-box lines, max three.
    var keys = [], handles = {};
    cart.items.slice().sort(function (a, b) { return b.final_line_price - a.final_line_price; }).forEach(function (i) {
      if (keys.length < 3 && keys.indexOf(i.product_id) === -1 && !OPEN_BOX.test(i.product_title)) { keys.push(i.product_id); handles[i.product_id] = i.handle; }
    });
    var picked = [], seen = {};
    function usable(p) { return p.available && !inCart[p.id] && !offered[p.id] && !seen[p.id] && !OPEN_BOX.test(p.title); }
    // Per key product: what customers actually bought with it (from orders), then the hand-picked complementary products.
    var lists = await Promise.all(keys.map(function (id) {
      return Promise.all([fetchBought(handles[id]), fetchRecs(base, id, 'complementary')]).then(function (r) {
        var ids = {};
        return r[0].concat(r[1]).filter(function (p) { if (ids[p.id]) return false; ids[p.id] = true; return true; });
      });
    }));
    // Round-robin so each key product contributes.
    for (var round = 0; picked.length < limit && round < 8; round++) {
      lists.forEach(function (list) { var p = list[round]; if (p && usable(p) && picked.length < limit) { picked.push(p); seen[p.id] = true; } });
    }
    if (picked.length < limit && box.dataset.fillRelated === 'true' && keys.length) {
      var related = await fetchRecs(base, keys[0], 'related');
      related.forEach(function (p) { if (picked.length < limit && usable(p)) { picked.push(p); seen[p.id] = true; } });
    }
    return picked;
  }

  async function renderRecs(box, cart) {
    var picked = await pickRecs(cart, box);
    if (!box.isConnected) return;
    box.querySelector('.a3b-recs__list').innerHTML = picked.map(function (p) { return recCard(p, box.dataset.quickAdd === 'true'); }).join('');
    box.hidden = picked.length === 0;
  }

  // Add button: Cart API, then the theme re-renders the drawer / basket page.
  document.addEventListener('click', async function (evt) {
    var btn = evt.target.closest('[data-a3b-quick-add]');
    if (!btn) return;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    try {
      var res = await fetch((theme.routes.cartAdd || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(btn.dataset.a3bQuickAdd), quantity: 1 }] })
      });
      var json = await res.json();
      if (!res.ok) throw new Error(json.description || json.message || 'Error');
      await refreshBasket(btn);
    } catch (e) {
      btn.disabled = false;
      btn.textContent = 'Add';
      var box = btn.closest('.a3b-rec');
      if (box && !box.querySelector('.a3b-rec__error')) box.querySelector('.a3b-rec__info').insertAdjacentHTML('beforeend', '<p class="a3b-rec__error" role="alert">' + esc(e.message) + '</p>');
    }
  });

  async function refreshBasket(from) {
    if (from && from.closest('cart-drawer')) {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      return;
    }
    var cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function') await cartItems.refresh();
    else window.location.reload();
  }

  /* ---------- 4. Goes well with (Kaching bundle) ---------- */
  var DEAL_TTL = 30 * 60 * 1000;

  function bundleSettingsIn(root, productId) {
    var found = null;
    root.querySelectorAll('script.kaching-bundles-deal-block-settings[data-product-id="' + productId + '"]').forEach(function (s) {
      if (found) return;
      try {
        var d = JSON.parse(s.textContent);
        if (d.dealBars && d.dealBars.some(function (b) { return b.dealBarType === 'bundle'; })) found = d;
      } catch (e) { /* ignore */ }
    });
    return found;
  }

  /** Bundle sales by bar id ({barId: orders}) written into the page from amp3.bundle_bar_sales; {} without data. */
  function barSalesIn(root, productId) {
    var el = root.querySelector('script[data-a3-bar-sales][data-product-id="' + productId + '"]');
    try { return (el && JSON.parse(el.textContent)) || {}; } catch (e) { return {}; }
  }

  /** Kaching's deal settings for a product: from this page if it's that product's page, else its ?view=a3-deal page (cached for the visit). */
  async function dealFor(item) {
    var local = bundleSettingsIn(document, item.product_id);
    if (local) return Object.assign({}, local, { sales: barSalesIn(document, item.product_id) });
    var key = 'a3b-deal:' + item.product_id;
    try {
      var cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.at < DEAL_TTL) return cached.deal;
    } catch (e) { /* storage unavailable */ }
    var url = '/products/' + item.handle + '?view=a3-deal';
    if (!memo[url]) {
      memo[url] = fetch(url).then(function (r) { return r.ok ? r.text() : ''; }).then(function (html) {
        var doc = html ? new DOMParser().parseFromString(html, 'text/html') : null;
        var deal = doc ? bundleSettingsIn(doc, item.product_id) : null;
        var keep = deal ? { dealBars: deal.dealBars, preselectedDealBarId: deal.preselectedDealBarId, otherProductHandles: deal.otherProductHandles, sales: barSalesIn(doc, item.product_id) } : null;
        try { sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), deal: keep })); } catch (e) { /* storage unavailable */ }
        return keep;
      }).catch(function () { delete memo[url]; return null; });
    }
    return memo[url];
  }

  function card(handle) { return getJSON('/products/' + handle + '?view=bundle-card'); }
  function inStock(v) { return v.available && (v.tracked === null || v.tracked === '' || v.qty > 0); }
  function shortName(title, vendor) {
    var t = String(title).split(/\s+for\s+(the\s+)?/i)[0];
    if (vendor) t = t.replace(new RegExp('^' + vendor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s+', 'i'), '');
    return t;
  }

  /** The offer for one basket line: its first usable bundle bar (best-selling, then Kaching's preselected, then deal order). */
  async function offerFor(item, inCart) {
    var deal = await dealFor(item);
    if (!deal) return null;
    var bars = deal.dealBars.filter(function (b) { return b.dealBarType === 'bundle'; });
    // Best-selling bundle first (real orders), then Kaching's preselected bar, then the deal's order.
    var sales = deal.sales || {};
    bars = bars.map(function (b, i) { return { b: b, i: i }; }).sort(function (x, y) {
      return ((sales[y.b.id] || 0) - (sales[x.b.id] || 0)) ||
        ((y.b.id === deal.preselectedDealBarId) - (x.b.id === deal.preselectedDealBarId)) || (x.i - y.i);
    }).map(function (x) { return x.b; });
    var cards = (await Promise.all((deal.otherProductHandles || []).map(card))).filter(Boolean);
    var byId = {};
    cards.forEach(function (c) { byId[c.id] = c; });
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i], extras = [], ok = true, saving = 0, known = true;
      (bar.bundleProducts || []).forEach(function (bp) {
        if (!ok) return;
        if (!bp.productGID || bp.productGID === 'default') {   // this basket item
          if (bp.discountType === 'percentage') saving += Math.floor(item.final_price * bp.discountValue / 100);
          else if (bp.discountType !== 'default') known = false;
          return;
        }
        var c = byId[Number(String(bp.productGID).split('/').pop())];
        if (!c || inCart[c.id]) { ok = false; return; }
        var v = c.variants.filter(inStock)[0];
        if (!v) { ok = false; return; }   // bundles are blocked when an extra isn't in stock
        // Kaching rounds a percentage discount down to the penny (£27.99 at 20% off = £22.40).
        var qty = bp.quantity || 1, off = 0;
        if (bp.discountType === 'percentage') off = Math.floor(v.price * bp.discountValue / 100);
        else if (bp.discountType !== 'default') known = false;
        saving += off * qty;
        extras.push({ card: c, variant: v, qty: qty, price: (v.price - off) * qty, full: v.price * qty });
      });
      if (ok && extras.length) return { bar: bar, item: item, extras: extras, saving: known ? saving : null };
    }
    return null;
  }

  function goesHTML(o) {
    var model = shortName(o.item.product_title, o.item.vendor).split(' - ')[0];
    var names = o.extras.map(function (e) { return shortName(e.card.title, e.card.vendor); }).join(' + ');
    var add = o.extras.reduce(function (t, e) { return t + e.price; }, 0);
    var full = o.extras.reduce(function (t, e) { return t + e.full; }, 0);
    var url = o.item.url.split('?')[0] + '?variant=' + o.item.variant_id + '&a3bar=' + encodeURIComponent(o.bar.id) + '&a3swap=' + o.item.variant_id + '#a3-bundles';
    var title = o.bar.title && o.bar.title.indexOf('{{') === -1 ? o.bar.title : 'Better together';
    return '<p class="a3b-gw__eyebrow">Goes well with your ' + esc(model) + '</p>' +
      '<div class="a3b-gw__body">' +
        '<div class="a3b-gw__images" aria-hidden="true">' + o.extras.slice(0, 2).map(function (e) {
          return '<span><img src="' + esc(sized(e.variant.image || e.card.image, 200)) + '" alt="" loading="lazy" width="72" height="72"></span>';
        }).join('') + '</div>' +
        '<div class="a3b-gw__text">' +
          '<p class="a3b-gw__title">' + esc(title) + ': add the ' + esc(names) + '</p>' +
          '<p class="a3b-gw__price"><strong>+' + money(add) + '</strong>' + (full > add ? ' <del>' + money(full) + '</del>' : '') +
            (o.saving > 0 ? ' <span class="a3b-gw__saving">Save ' + money(o.saving) + ' as a bundle</span>' : '') + '</p>' +
          '<a class="a3b-gw__cta" href="' + esc(url) + '">Make it a bundle<span class="a3-sr-only">: ' + esc(title) + '</span> →</a>' +
        '</div>' +
      '</div>';
  }

  async function renderGoes(box, cart) {
    var inCart = {};
    cart.items.forEach(function (i) { inCart[i.product_id] = true; });
    // Highest line value first; lines already in a bundle and open-box lines are skipped. Max two lookups.
    var lines = cart.items.filter(function (i) { return !isBundleLine(i) && !OPEN_BOX.test(i.product_title) && i.product_type !== 'Gift card'; })
      .sort(function (a, b) { return b.final_line_price - a.final_line_price; }).slice(0, 2);
    var offer = null;
    for (var i = 0; i < lines.length && !offer; i++) offer = await offerFor(lines[i], inCart);
    offered = {};
    if (offer) offer.extras.forEach(function (e) { offered[e.card.id] = true; });
    if (!box.isConnected) return;
    box.innerHTML = offer ? goesHTML(offer) : '';
    box.hidden = !offer;
  }

  /* ---------- filling the boxes ---------- */
  // The theme re-renders the drawer / cart items after every change, so the boxes come back empty:
  // fill any unfilled box. Results are cached per page view, so a refill is quick.
  var filling = false, again = false;
  async function fillBoxes() {
    if (filling) { again = true; return; }
    // Drawer boxes wait until the drawer is open, so pages don't fetch offers nobody sees.
    var boxes = Array.prototype.filter.call(document.querySelectorAll('[data-a3b-recs], [data-a3b-goes]'), function (b) {
      var drawer = b.closest('cart-drawer');
      return !b.hasAttribute('data-a3b-filled') && (!drawer || drawer.hasAttribute('open'));
    });
    if (!boxes.length) return;
    filling = true;
    boxes.forEach(function (b) { b.setAttribute('data-a3b-filled', ''); });
    try {
      var cart = await getCart();
      if (cart.item_count > 0) {
        // The bundle offer first, so its products can be left out of "Customers also choose".
        var goes = boxes.filter(function (b) { return b.hasAttribute('data-a3b-goes'); });
        var goesShown = document.querySelector('[data-a3b-goes]');
        if (!goes.length && !goesShown) offered = {};
        await Promise.all(goes.map(function (b) { return renderGoes(b, cart); }));
        await Promise.all(boxes.filter(function (b) { return b.hasAttribute('data-a3b-recs'); }).map(function (b) { return renderRecs(b, cart); }));
      }
    } catch (e) { /* leave hidden */ }
    filling = false;
    if (again) { again = false; fillBoxes(); }
  }
  function refillAll() {
    document.querySelectorAll('[data-a3b-filled]').forEach(function (b) { b.removeAttribute('data-a3b-filled'); });
    fillBoxes();
  }
  var fillTimer;
  new MutationObserver(function () { clearTimeout(fillTimer); fillTimer = setTimeout(fillBoxes, 60); })
    .observe(document.documentElement, { childList: true, subtree: true });
  // Changes that don't re-render the boxes (e.g. the basket page's own recommendations) still refresh them.
  document.addEventListener('on:line-item:change', function () { setTimeout(refillAll, 300); });
  document.addEventListener('on:cart:add', function () { setTimeout(refillAll, 300); });

  function init() { watchDrawer(); fillBoxes(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
